/**
 * services/adminAdsApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin-only ad plan and validity management methods.
 *
 * METHODS:
 *   adminAdjustValidity(adId, days)                — extend/shorten ad validity
 *   adminGetAdPlan(adId)                           — fetch current plan for an ad
 *   adminAssignAdPlan(adId, {plan_type, amount_paid, ...}) — assign/upgrade plan
 *   adminRemoveAdPlan(adId)                        — remove plan, reset to free
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async adminAdjustValidity(adId, days) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/admin/products/${adId}/adjust-validity/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ days }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || `HTTP ${res.status}` };
      return { success: true, days_adjusted: data.days_adjusted, product: data.product };
    } catch (error) { return { success: false, error: error.message || 'Network error' }; }
  },
// ADMIN — AD PLAN ASSIGNMENT

  async adminGetAdPlan(adId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'No token' };
      const res = await fetch(`${API_BASE_URL}/admin/products/${adId}/plan/`, {
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || `HTTP ${res.status}` };
      return { success: true, ...data };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async adminAssignAdPlan(adId, { plan_type, amount_paid, payment_reference, expires_at } = {}) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'No token' };
      const res = await fetch(`${API_BASE_URL}/admin/products/${adId}/plan/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_type, amount_paid, payment_reference, expires_at }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || `HTTP ${res.status}` };
      return { success: true, ...data };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async adminRemoveAdPlan(adId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'No token' };
      const res = await fetch(`${API_BASE_URL}/admin/products/${adId}/plan/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || `HTTP ${res.status}` };
      return { success: true, ...data };
    } catch (error) { return { success: false, error: error.message }; }
  }

  // ADMIN — MESSAGING
};